"use client"

import { useEffect } from "react"
import { LoadingOverlay } from "@mantine/core"
import { useAuth } from "./AuthProvider"

export interface AuthGuardTranslations {
  loading: string
  unauthorized: string
  accessDenied: string
}

export interface AuthGuardProps {
  children: React.ReactNode
  requireAuth?: boolean
  allowedRoles?: string[]
  fallback?: React.ReactNode
  onUnauthorized?: () => void
  translations?: Partial<AuthGuardTranslations>
  className?: string
}

// Default English translations as fallback
const defaultTranslations: AuthGuardTranslations = {
  loading: "Loading...",
  unauthorized: "Unauthorized access",
  accessDenied: "Access denied. Insufficient permissions."
}

export function AuthGuard({
  children,
  requireAuth = true,
  allowedRoles,
  fallback,
  onUnauthorized,
  translations = {},
  className = ""
}: AuthGuardProps) {
  const { isAuthenticated, user, isLoading } = useAuth()

  // Merge with default translations
  const t = { ...defaultTranslations, ...translations }

  useEffect(() => {
    if (!isLoading && requireAuth && !isAuthenticated) {
      onUnauthorized?.()
    }
  }, [isAuthenticated, isLoading, requireAuth, onUnauthorized])

  if (isLoading) {
    return (
      <div className={className}>
        <LoadingOverlay visible />
      </div>
    )
  }

  if (requireAuth && !isAuthenticated) {
    return (
      <div className={className}>
        {fallback || (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                {t.unauthorized}
              </h2>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return (
      <div className={className}>
        {fallback || (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                {t.accessDenied}
              </h2>
            </div>
          </div>
        )}
      </div>
    )
  }

  return <div className={className}>{children}</div>
}
